import React, { FC, useMemo, useState, useEffect } from 'react'
import { View, Image } from 'react-native'
import { useFragment, graphql } from 'react-relay'
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  interpolate,
  withSpring,
} from 'react-native-reanimated'

import { useLocation } from 'react-router-dom'

import { Trans } from '@lingui/react/macro'

import { getPurpleBoxShadow } from './UnauthenticatedHomeScreen/styles'

import { Text, YStack, useThemeName } from 'tamagui'

import { Grid } from '@newComponents/Grid'
import { Link } from '@newComponents/Link'
import { useBreakpointIndex } from '@newComponents/useBreakpointValue'

import { HOST_NAME } from '@data/config/url'

import { HomeScreenPresentationsCard_query$key } from './__generated__/HomeScreenPresentationsCard_query.graphql'

const PRESENTATION_CARD_LAYOUT_WIDTH = 392

export const HomeScreenPresentationsCard: FC<{
  query: HomeScreenPresentationsCard_query$key
}> = ({ query }) => {
  const data = useFragment(
    graphql`
      fragment HomeScreenPresentationsCard_query on Query {
        presentationsCard: presentations(
          first: 12
          orderBy: [CREATED_AT_DESC, ID_ASC]
        ) {
          edges {
            node {
              prettyUrl

              presenter {
                id
              }
            }

            cursor
          }
        }
      }
    `,
    query
  )

  const isDark = useThemeName() === 'dark'
  const purpleBoxShadow = getPurpleBoxShadow(isDark)
  const purpleBorderColor = isDark ? 'rgba(167, 139, 250, 0.15)' : 'rgba(85, 51, 235, 0.2)'

  // Load 12 Presentations and filter them down by unique Presenters
  const presentations = useMemo(() => {
    if (!data.presentationsCard) {
      return null
    }

    const presentations = data.presentationsCard

    const filteredPresentations = presentations.edges.reduce<
      typeof presentations.edges
    >(
      (memo, edge, index) => {
        if (memo.length >= 4) {
          return memo
        }

        if (edge.node.presenter) {
          const presenter = edge.node.presenter

          const indexOfFirstByPresenter = presentations.edges.findIndex((e) => {
            if (e.node.presenter) {
              return e.node.presenter.id === presenter.id
            }

            return false
          })

          if (indexOfFirstByPresenter !== index) {
            return memo
          }
        }

        return [...memo, edge]
      },
      []
    )

    if (filteredPresentations.length >= 4) {
      return filteredPresentations
    }

    // Fallback to unfiltered data if not enough
    return data.presentationsCard.edges.slice(0, 4)
  }, [data.presentationsCard])

  const imageURLs = useMemo(() => {
    if (!presentations) {
      return ['', '', '', '']
    }

    return presentations.map((edge) => {
      return `${HOST_NAME}/social/presentation/${edge.node.prettyUrl}/card.svg`
    })
  }, [presentations])

  const urls = useMemo(() => {
    if (!presentations) {
      return ['', '', '', '']
    }

    return presentations.map((edge) => {
      return `/presentations/${edge.node.prettyUrl}`
    })
  }, [presentations])

  const breakpoint = useBreakpointIndex()

  const [cardPosition, setCardPositions] = useState([0, 1, 2, 3])

  const cardWidth = useMemo(() => {
    const values = [280, 300, 320, 280]
    return values[Math.min(breakpoint, values.length - 1)]
  }, [breakpoint])

  const cardPositions = useMemo(() => {
    const bpVal = <T,>(values: T[]): T => values[Math.min(breakpoint, values.length - 1)]

    return [
      {
        x: (PRESENTATION_CARD_LAYOUT_WIDTH - cardWidth) / 2,
        y: 40,
        z: 0,
        blur: 0,
        opacity: 1.0,
        zIndex: 10,
      },
      {
        x:
          (PRESENTATION_CARD_LAYOUT_WIDTH - cardWidth) / 2 +
          bpVal([
            cardWidth,
            cardWidth * 1.2,
            cardWidth * 1.2,
            cardWidth,
          ]),
        y: bpVal([
          cardWidth * 0.7,
          cardWidth * 0.55,
          cardWidth * 0.5,
          cardWidth * 0.7,
        ]),
        z: -50,
        blur: 0.5,
        opacity: 0.9,
        zIndex: 0,
      },
      {
        x:
          (PRESENTATION_CARD_LAYOUT_WIDTH - cardWidth) / 2 -
          bpVal([
            cardWidth * 1.2,
            cardWidth * 1.2,
            cardWidth * 1.3,
            cardWidth * 1.1,
          ]),
        y: cardWidth * -0.07,
        z: -100,
        blur: 1,
        opacity: 0.8,
        zIndex: 0,
      },

      {
        x:
          (PRESENTATION_CARD_LAYOUT_WIDTH - cardWidth) / 2 -
          bpVal([
            cardWidth * 1.2,
            cardWidth * 1.2,
            cardWidth * 1.4,
            cardWidth,
          ]),
        y: bpVal([
          cardWidth,
          cardWidth,
          cardWidth * 0.9,
          cardWidth * 1.1,
        ]),
        z: -150,
        blur: 1.5,
        opacity: 0.7,
        zIndex: 0,
      },
    ]
  }, [breakpoint, cardWidth])

  const mainCircleSize = useSharedValue(32)
  const circle1Size = useSharedValue(56)
  const circle2Size = useSharedValue(84)
  const circle3Size = useSharedValue(120)

  const mainCircleOpacity = useSharedValue(1)
  const circle1Opacity = useSharedValue(1)
  const circle2Opacity = useSharedValue(1)
  const circle3Opacity = useSharedValue(1)

  useEffect(() => {
    const springOptions = { mass: 1, damping: 100, stiffness: 50 }

    const intervalRef = setInterval(() => {
      setCardPositions((cardPositions) => {
        return [...cardPositions.slice(1), cardPositions[0]]
      })

      mainCircleOpacity.value = withSpring(0, springOptions)
      circle1Opacity.value = withSpring(0, springOptions)
      circle2Opacity.value = withSpring(0, springOptions)
      circle3Opacity.value = withSpring(0, springOptions)

      mainCircleSize.value = withSpring(32 * 2, springOptions)
      circle1Size.value = withSpring(56 * 2, springOptions)
      circle2Size.value = withSpring(84 * 2, springOptions)
      circle3Size.value = withSpring(120 * 2, springOptions, () => {
        mainCircleSize.value = 0
        circle1Size.value = 0
        circle2Size.value = 0
        circle3Size.value = 0

        mainCircleOpacity.value = withSpring(1, springOptions)
        circle1Size.value = withSpring(32, springOptions)
        circle2Size.value = withSpring(32, springOptions)
        circle3Size.value = withSpring(32, springOptions)
        mainCircleSize.value = withSpring(32, springOptions, () => {
          circle1Size.value = 0
          circle1Opacity.value = withSpring(1, springOptions)
          circle2Size.value = withSpring(56, springOptions)
          circle3Size.value = withSpring(56, springOptions)
          circle1Size.value = withSpring(56, springOptions, () => {
            circle2Size.value = 0
            circle2Opacity.value = withSpring(1, springOptions)
            circle3Size.value = withSpring(84, springOptions)
            circle2Size.value = withSpring(84, springOptions, () => {
              circle3Size.value = 0
              circle3Opacity.value = withSpring(1, springOptions)
              circle3Size.value = withSpring(120, springOptions)
            })
          })
        })
      })
    }, 10000)

    return () => {
      clearInterval(intervalRef)
    }
  }, [setCardPositions])

  // @ts-ignore useAnimatedStyle returns web-only CSS properties (background, boxShadow, borderRadius: '50%')
  const mainCircleStyle = useAnimatedStyle(() => {
    return {
      position: 'absolute',

      background: isDark
        ? 'linear-gradient(358.66deg, rgba(139, 92, 246, 1.0) -0.85%, rgba(167, 139, 250, 1.0) 96.87%)'
        : 'linear-gradient(358.66deg, rgba(85, 51, 235, 1.0) -0.85%, rgba(110, 51, 235, 1.0) 96.87%)',
      boxShadow: isDark
        ? 'inset 0px -2px 3px rgba(0, 0, 0, 0.4), 0px 0px 12px rgba(139, 92, 246, 0.5)'
        : 'inset 0px -2px 3px rgba(0, 0, 0, 0.25)',

      borderRadius: '50%',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 20,

      transform: [
        {
          perspective: 100,
        },
        {
          translateX:
            (PRESENTATION_CARD_LAYOUT_WIDTH - cardWidth) / 2 +
            cardWidth -
            mainCircleSize.value / 2,
        },
        { translateY: 40 - mainCircleSize.value / 2 },
      ],

      width: mainCircleSize.value,
      height: mainCircleSize.value,

      opacity: mainCircleOpacity.value,
    }
  }, [isDark])

  // @ts-ignore useAnimatedStyle returns web-only CSS properties
  const circle1Style = useAnimatedStyle(() => {
    return {
      position: 'absolute',

      background: isDark
        ? `linear-gradient(180deg, rgba(139, 92, 246, ${0.7 * 0.45}) 0%, rgba(139, 92, 246, ${0.2 * 0.45}) 100%)`
        : `linear-gradient(180deg, rgba(85, 51, 235, ${0.7 * 0.35}) 0%, rgba(85, 51, 235, ${0.2 * 0.35}) 100%)`,

      borderWidth: 1,
      borderColor: isDark
        ? `rgba(167, 139, 250, ${0.2 * 0.5})`
        : `rgba(27, 19, 64, ${0.2 * 0.35})`,
      borderRadius: '50%',

      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 20,

      transform: [
        {
          perspective: 100,
        },
        {
          translateX:
            (PRESENTATION_CARD_LAYOUT_WIDTH - cardWidth) / 2 +
            cardWidth -
            circle1Size.value / 2,
        },
        { translateY: 40 - circle1Size.value / 2 },
      ],

      width: circle1Size.value,
      height: circle1Size.value,

      opacity: circle1Opacity.value,
    }
  }, [isDark])

  // @ts-ignore useAnimatedStyle returns web-only CSS properties
  const circle2Style = useAnimatedStyle(() => {
    return {
      position: 'absolute',

      background: isDark
        ? `linear-gradient(180deg, rgba(139, 92, 246, ${0.7 * 0.4}) 0%, rgba(139, 92, 246, ${0.2 * 0.4}) 100%)`
        : `linear-gradient(180deg, rgba(85, 51, 235, ${0.7 * 0.3}) 0%, rgba(85, 51, 235, ${0.2 * 0.3}) 100%)`,
      borderWidth: 1,
      borderColor: isDark
        ? `rgba(167, 139, 250, ${0.2 * 0.4})`
        : `rgba(27, 19, 64, ${0.2 * 0.3})`,
      borderRadius: '50%',

      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 0,

      transform: [
        {
          perspective: 100,
        },
        {
          translateX:
            (PRESENTATION_CARD_LAYOUT_WIDTH - cardWidth) / 2 +
            cardWidth -
            circle2Size.value / 2,
        },
        { translateY: 40 - circle2Size.value / 2 },
      ],

      width: circle2Size.value,
      height: circle2Size.value,

      opacity: circle2Opacity.value,
    }
  }, [isDark])

  // @ts-ignore useAnimatedStyle returns web-only CSS properties
  const circle3Style = useAnimatedStyle(() => {
    return {
      position: 'absolute',

      background: isDark
        ? `linear-gradient(180deg, rgba(167, 139, 250, ${0.25}) 0%, rgba(167, 139, 250, ${0.25 * 0.3}) 100%)`
        : `linear-gradient(180deg, rgba(229, 173, 255, ${0.2}) 0%, rgba(229, 173, 255, ${0.2 * 0.2}) 100%)`,
      borderWidth: 1,
      borderColor: isDark
        ? `rgba(167, 139, 250, ${0.2 * 0.3})`
        : `rgba(27, 19, 64, ${0.2 * 0.2})`,
      borderRadius: '50%',

      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 0,

      transform: [
        {
          perspective: 100,
        },
        {
          translateX:
            (PRESENTATION_CARD_LAYOUT_WIDTH - cardWidth) / 2 +
            cardWidth -
            circle3Size.value / 2,
        },
        { translateY: 40 - circle3Size.value / 2 },
      ],

      width: circle3Size.value,
      height: circle3Size.value,

      opacity: circle3Opacity.value,
    }
  }, [isDark])

  return (
    <YStack padding="$6" overflow="hidden" backgroundColor="$backgroundSoft" borderColor={purpleBorderColor} borderWidth={0.25} borderRadius="$8" flex={1} style={{ boxShadow: purpleBoxShadow } as any}>
      <YStack gap="$4">
        <YStack gap="$2">
          <Text fontSize="$7" fontWeight="700" fontFamily="$body">
            <Trans>Presentations</Trans>
          </Text>

          <Grid>
            <Grid.Column flex={breakpoint >= 2 ? 5 : 1}>
              <View style={{ zIndex: 20 }}>
                <Text fontSize="$4" fontWeight="500" fontFamily="$body">
                  <Trans>Missed an Event? Catch the Presentations any time!</Trans>{' '}
                  <Text fontSize="$4" color="$colorMuted">
                    <Trans>As a Presenter, your portfolio lives on Guild. Communities
                    can scout you for content, further expanding your reach.
                    It's like having you own personal fan club!</Trans>
                  </Text>
                </Text>
              </View>
            </Grid.Column>

            <Grid.Column flex={breakpoint >= 2 ? 1 : 0} display={breakpoint >= 2 ? 'flex' : 'none'} />
          </Grid>
        </YStack>

        <YStack alignItems='center'>
          <View
            style={{ height: 200, width: PRESENTATION_CARD_LAYOUT_WIDTH }}
          >
            <Animated.View style={circle3Style} />

            <Animated.View style={circle2Style} />

            <Animated.View style={circle1Style} />

            <Animated.View style={mainCircleStyle} />

            <PresentationCard
              photoURL={imageURLs[0]}
              url={urls[0]}
              cardPosition={cardPositions[cardPosition[0]]}
              cardWidth={cardWidth}
              isDark={isDark}
            />

            <PresentationCard
              photoURL={imageURLs[1]}
              url={urls[1]}
              cardPosition={cardPositions[cardPosition[1]]}
              cardWidth={cardWidth}
              isDark={isDark}
            />

            <PresentationCard
              photoURL={imageURLs[2]}
              url={urls[2]}
              cardPosition={cardPositions[cardPosition[2]]}
              cardWidth={cardWidth}
              isDark={isDark}
            />

            <PresentationCard
              photoURL={imageURLs[3]}
              url={urls[3]}
              cardPosition={cardPositions[cardPosition[3]]}
              cardWidth={cardWidth}
              isDark={isDark}
            />
          </View>
        </YStack>
      </YStack>
    </YStack>
  )
}

const PresentationCard: FC<{
  photoURL: string
  url: string
  cardPosition: {
    x: number
    y: number
    z: number
    blur: number
    opacity: number
    zIndex: number
  }
  cardWidth: number
  isDark: boolean
}> = ({ photoURL, url, cardPosition, cardWidth, isDark }) => {
  const x = useSharedValue(cardPosition.x)
  const y = useSharedValue(cardPosition.y)
  const z = useSharedValue(cardPosition.z)
  const blur = useSharedValue(cardPosition.blur)
  const opacity = useSharedValue(cardPosition.opacity)
  const zIndex = useSharedValue(cardPosition.zIndex)

  const location = useLocation()

  // @ts-ignore useAnimatedStyle returns web-only CSS properties (translateZ, filter)
  const animatedStyle = useAnimatedStyle(() => {
    const shadowColor = isDark
      ? `rgba(0, 0, 0, ${interpolate(z.value, [-150, 0], [0.2, 0.6])})`
      : `rgba(10, 37, 64, ${interpolate(z.value, [-150, 0], [0.1, 0.4])})`
    const glowShadow = isDark
      ? `, 0px 0px ${interpolate(z.value, [-150, 0], [4, 16])}px rgba(139, 92, 246, ${interpolate(z.value, [-150, 0], [0.05, 0.2])})`
      : ''

    return {
      borderRadius: 16,
      position: 'absolute',

      transform: [
        { perspective: 100 },

        {
          translateX: x.value,
        },

        { translateY: y.value },
        { translateZ: z.value },
      ],
      opacity: opacity.value,
      filter: `blur(${blur.value}px)`,
      zIndex: zIndex.value,
      boxShadow: `0px ${interpolate(z.value, [-150, 0], [2, 15])}px ${interpolate(z.value, [-150, 0], [5, 20])}px ${shadowColor}${glowShadow}`,
    }
  }, [isDark])

  useEffect(() => {
    const springOptions = { mass: 1, damping: 100, stiffness: 100 }

    zIndex.value = cardPosition.zIndex

    x.value = withSpring(cardPosition.x, springOptions)
    y.value = withSpring(cardPosition.y, springOptions)
    z.value = withSpring(cardPosition.z, springOptions)
    blur.value = withSpring(cardPosition.blur, springOptions)
    opacity.value = withSpring(cardPosition.opacity, springOptions)
  }, [cardPosition])

  return (
    <Animated.View style={animatedStyle}>
      <Link href={url} options={{ state: { backgroundLocation: location } }}>
        <Image
          source={{
            uri: photoURL,
          }}
          style={{
            width: cardWidth,
            height: cardWidth / 2,
            borderRadius: 16,
          }}
        />
      </Link>
    </Animated.View>
  )
}
